/**
 * Lane C0-PROPORTION, B3 hands (user 2026-10-11: "why are the hands lumps?"). Cause: dressed figures on the new body
 * draw each hand at art resolution (newBody.ts `mitten()`, a 3×4 block `sss/Sss/Sss/eee`, or `handArt`) and the frame is
 * enlarged with Scale2x, so the hand ends as a 6×8 skin block with no thumb or knuckles; only the bare body lays the
 * full-resolution hand (hands.ts `handSprite`). Not the B3 head shrink (hands are not head pieces).
 * Here those art-resolution hand pieces are swapped for the full-resolution hand: the same shape the pose asks for
 * (`handShape`: hanging, fist, grip, open palm, clasp), with its thumb, knuckle row, two skin tones and its own line, laid
 * in like the bare body's hands (realism.ts: over what lies behind, under what lies in front), its art footprint taken
 * from the same sprite (hands.ts `drawHandArt`). Where the body hides the forearm (newBody.ts `forearmHidden`) the
 * mitten draws nothing, and so does this.
 */
import type { Piece } from '../figure';
import { RIG_RES } from '../figure';
import { drawHand, drawHandArt, handShape, handSprite, type HandSprite, handTones } from '../hands';
import type { Side, Skeleton } from '../skeleton';

let ON = false;
/** Full-resolution hands on dressed B3 figures: off until reviewed; review tools turn it on. */
export function setFineHands(v: boolean): void {
  ON = v;
}
export const fineHandsOn = (): boolean => ON;

/** Whether an art-resolution drawing puts down any pixel (run on a stand-in that only counts). */
function drawsAnything(draw: (g: CanvasRenderingContext2D) => void): boolean {
  let n = 0;
  const probe = { fillStyle: '', fillRect: () => void n++ } as unknown as CanvasRenderingContext2D;
  try {
    draw(probe);
  } catch {
    return true;
  }
  return n > 0;
}

/**
 * The body layer's pieces with the mitten hands made full-resolution hands. The new body pushes, per side B then A, an
 * arm and then its hand, both with a depth getter (`armZ`, the hand 0.08 nearer): the hand is the getter piece right after
 * an arm getter piece whose depth is 0.08 less.
 */
export function withFineHands(
  pieces: readonly Piece[],
  sk: Skeleton,
  skin: { skin: string; skinS: string },
  getter: (q: Piece) => boolean,
): Piece[] {
  if (!ON) return [...pieces];
  const tones = handTones(skin.skin, skin.skinS);
  const sides: Side[] = ['B', 'A'];
  let k = 0;
  let prev: Piece | null = null;
  return pieces.map((q) => {
    if (!getter(q) || q.hand && !q.grip) {
      prev = null;
      return q;
    }
    const isHand = prev !== null && Math.abs(q.z - (prev as Piece).z - 0.08) < 1e-6;
    prev = q;
    if (!isHand) return q;
    const side = sides[k++] ?? 'A';
    const shape = q.grip ? 'grip' : handShape(sk, side, false);
    let sprite: HandSprite | undefined;
    const sp = () => (sprite ??= handSprite(sk, side, shape, tones, RIG_RES));
    let shown: boolean | undefined;
    const visible = () => (shown ??= drawsAnything(q.draw));
    return {
      z: q.z,
      hand: true,
      ...(q.grip && { grip: true }),
      draw: (g) => {
        if (visible()) drawHandArt(g, sp(), RIG_RES);
      },
      fine: (g, res) => {
        if (visible()) drawHand(g, res === RIG_RES ? sp() : handSprite(sk, side, shape, tones, res));
      },
    };
  });
}
